import { Link, router } from '@inertiajs/react';
import { Plus, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { agentHueStyle } from '@/frontend/lib/agentHue';
import { shouldSkipRowNavigation } from '@/frontend/utils/clickableRow';

const initialOf = (name: string) => name.trim().charAt(0).toUpperCase();

interface Worker {
  id: string;
  name: string;
  job_title: string | null;
  avatar_url: string | null;
}

interface AssignedWorkersBannerProps {
  workers: Worker[];
  onAddWorker?: () => void;
  onRemoveWorker?: (workerId: string) => void;
}

export default function AssignedWorkersBanner({ workers, onAddWorker, onRemoveWorker }: AssignedWorkersBannerProps) {
  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Users className="h-4 w-4" />
            Colaboradores digitais ({workers.length})
          </div>
          {onAddWorker && (
            <Button variant="outline" size="sm" onClick={onAddWorker}>
              <Plus className="h-4 w-4" />
              Adicionar
            </Button>
          )}
        </div>

        {workers.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Nenhum colaborador digital usa esta integração ainda.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead className="hidden sm:table-cell">Cargo</TableHead>
                {onRemoveWorker && <TableHead className="w-10" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {workers.map((worker) => (
                <TableRow
                  key={worker.id}
                  className="cursor-pointer"
                  onClick={(e) => {
                    if (shouldSkipRowNavigation(e)) return;
                    router.visit(`/dashboard/digital_workers/${worker.id}`);
                  }}
                >
                  <TableCell className="whitespace-normal">
                    <Link
                      href={`/dashboard/digital_workers/${worker.id}`}
                      className="inline-flex items-center gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Avatar className="size-8 shrink-0 text-[12px]">
                        {worker.avatar_url ? <AvatarImage alt="" src={worker.avatar_url} /> : null}
                        <AvatarFallback className="bg-agent text-agent-foreground" style={agentHueStyle(worker.id)}>
                          {initialOf(worker.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{worker.name}</span>
                    </Link>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-muted-foreground">
                    {worker.job_title || '—'}
                  </TableCell>
                  {onRemoveWorker && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => onRemoveWorker(worker.id)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
